import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Check, Send, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import LogoWordmark from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { submitReview } from "@/lib/api";

const RATING_LABELS = ["", "Not great", "Okay", "Good", "Really good", "Amazing"];

export default function ReviewPage() {
  const [form, setForm] = useState({ name: "", location: "", rating: 0, text: "" });
  const [hoverRating, setHoverRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const activeRating = hoverRating || form.rating;

  const submit = async (e) => {
    e.preventDefault();
    if (form.rating < 1) {
      toast.error("Tap the stars to rate your detail first");
      return;
    }
    if (form.name.trim().length < 2 || form.text.trim().length < 10) {
      toast.error("Add your name and a quick note (10+ characters)");
      return;
    }
    setLoading(true);
    try {
      await submitReview(form);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100svh] bg-[#0A0E17] flex flex-col" data-testid="review-page">
      <div className="mx-auto w-full max-w-xl px-4 sm:px-6 pt-8 pb-16 flex-1 flex flex-col">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="review-page-logo"><LogoWordmark /></Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#9CA3AF] hover:text-[#7CB342] transition-colors"
            data-testid="review-back-home"
          >
            <ArrowLeft size={15} /> Back to site
          </Link>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex-1 flex flex-col items-center justify-center text-center py-20"
            data-testid="review-success"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.15 }}
              className="w-20 h-20 rounded-full bg-[#7CB342]/15 border-2 border-[#7CB342] flex items-center justify-center mb-6"
            >
              <Check size={36} className="text-[#7CB342]" strokeWidth={3} />
            </motion.div>
            <h1 className="font-display font-black text-3xl sm:text-4xl text-[#F5F1E8] tracking-tight">
              You're the best — thank you!
            </h1>
            <p className="mt-4 text-[#9CA3AF] leading-relaxed max-w-sm">
              Your review is in. Dylan gives every review a quick read before it goes live,
              so look for it on the site soon.
            </p>
            <Link
              to="/"
              className="btn-pickle mt-8 inline-flex items-center justify-center rounded-full px-7 py-3.5 text-sm"
              data-testid="review-success-home-btn"
            >
              Back to Dillpickle's
            </Link>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-10 bg-[#111827] rounded-3xl border border-white/10 p-6 sm:p-10"
            data-testid="review-page-form"
          >
            <div className="text-center">
              <div className="label-eyebrow mb-3">Leave a Review</div>
              <h1 className="font-display font-black text-3xl sm:text-4xl text-[#F5F1E8] tracking-tight">
                How'd your <span className="text-[#7CB342] italic">shine</span> turn out?
              </h1>
              <p className="mt-3 text-sm text-[#9CA3AF]">Takes about 20 seconds — we read every single one.</p>
            </div>

            <div className="mt-9 flex flex-col items-center" onMouseLeave={() => setHoverRating(0)}>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <motion.button
                    key={n}
                    type="button"
                    whileTap={{ scale: 0.85 }}
                    onMouseEnter={() => setHoverRating(n)}
                    onClick={() => setForm({ ...form, rating: n })}
                    data-testid={`review-star-${n}`}
                    className="p-1.5 transition-transform hover:scale-115"
                    aria-label={`Rate ${n} stars`}
                  >
                    <Star
                      size={46}
                      className={`${activeRating >= n ? "fill-[#7CB342] text-[#7CB342] drop-shadow-[0_0_10px_rgba(124,179,66,0.4)]" : "text-[#9CA3AF]/30"} transition-colors`}
                    />
                  </motion.button>
                ))}
              </div>
              <div className="mt-2 h-5 text-sm font-bold text-[#7CB342]" data-testid="review-rating-label">
                {RATING_LABELS[activeRating] || ""}
              </div>
            </div>

            <div className="mt-7 space-y-5">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Your name</label>
                <Input
                  data-testid="review-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane D."
                  className="mt-2 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8]"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Your review</label>
                <Textarea
                  data-testid="review-text-input"
                  rows={4}
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder="What did you love? The shine, the smell, the convenience..."
                  className="mt-2 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8]"
                />
              </div>
              <button
                type="submit"
                data-testid="review-submit-btn"
                disabled={loading}
                className="btn-pickle w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-base disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Sending..." : "Send My Review"}
              </button>
              <p className="text-xs text-[#9CA3AF] text-center leading-relaxed">
                Dylan reads every review before it goes live — usually published within 24 hours.
              </p>
            </div>
          </motion.form>
        )}
      </div>
    </div>
  );
}
